import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Toast, Popup, Picker } from 'react-vant'
import { ArrowLeft } from '@react-vant/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getMyClasses, getAllClasses, getClassStudents, getCurrentAcademicYear, getAcademicYears } from '../../api/academic'
import { getPhysicalTests, batchImportTests } from '../../api/physicalTest'
import { isAdmin, isStaff, canManagePhysicalTest } from '../../utils/permissions'
import SportScoreKeyboard from '../../components/SportScoreKeyboard'
import {
  TEST_ITEMS,
  getStandardGradeName,
  checkStudentScorePass,
  parseTimeToSeconds
} from '../../utils/standardsScorer'

export default function BulkRecord() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialClassId = searchParams.get('classId')

  const { userInfo } = useSelector(state => state.user)
  const hasAccess = isAdmin(userInfo) || isStaff(userInfo) || canManagePhysicalTest(userInfo)

  // 学年与班级列表
  const [academicYears, setAcademicYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('')
  const [showYearPicker, setShowYearPicker] = useState(false)

  const [classList, setClassList] = useState([])
  const [selectedClass, setSelectedClass] = useState(null)
  const [showClassPicker, setShowClassPicker] = useState(false)

  // 当前选中测试项目 (默认 50米跑 或 身高/体重)
  const [selectedItemKey, setSelectedItemKey] = useState('run_50m')
  const [showItemPicker, setShowItemPicker] = useState(false)

  // 模式：全班模式 (class) 或 分组循环模式 (group)
  const [entryMode, setEntryMode] = useState('class') // 'class' | 'group'
  const [groupSize, setGroupSize] = useState(10)
  const [activeGroupIndex, setActiveGroupIndex] = useState(0)

  // 学生与成绩数据
  const [students, setStudents] = useState([])
  const [loadingStudents, setLoadingStudents] = useState(false)
  const [allRecords, setAllRecords] = useState({}) // student_id -> record obj
  const [scores, setScores] = useState({}) // student_id -> scoreVal (string or {height, weight})
  const [initialScores, setInitialScores] = useState({}) // student_id -> original scoreVal

  // 键盘与焦点控制
  const [activeStudentId, setActiveStudentId] = useState(null)
  const [activeSubField, setActiveSubField] = useState('height') // for compound: 'height' | 'weight'
  const [showKeyboard, setShowKeyboard] = useState(false)

  const [saving, setSaving] = useState(false)

  // 获取当前项目对象
  const selectedItem = useMemo(() => {
    return TEST_ITEMS.find(it => it.key === selectedItemKey) || TEST_ITEMS[2]
  }, [selectedItemKey])

  // 1. 初始化学年与班级
  useEffect(() => {
    const init = async () => {
      try {
        const [currRes, yearsRes, classesRes] = await Promise.allSettled([
          getCurrentAcademicYear(),
          getAcademicYears(),
          getMyClasses({ page_size: 200 }),
        ])

        let yList = []
        if (yearsRes.status === 'fulfilled' && yearsRes.value) {
          const val = yearsRes.value
          if (Array.isArray(val)) yList = val
          else if (val.options) yList = val.options.map(o => String(o.value || o))
          else if (val.years) yList = val.years
        }
        if (!yList.length) {
          const now = new Date()
          const yr = now.getFullYear()
          yList = [now.getMonth() >= 7 ? `${yr}-${yr + 1}` : `${yr - 1}-${yr}`]
        }
        setAcademicYears(yList)

        let currYear = localStorage.getItem('current_academic_year')
        if (!currYear || !yList.includes(currYear)) {
          if (currRes.status === 'fulfilled' && currRes.value?.current_academic_year) {
            currYear = currRes.value.current_academic_year
          } else {
            currYear = yList[0]
          }
        }
        setSelectedYear(currYear)

        let cItems = []
        if (classesRes.status === 'fulfilled' && classesRes.value) {
          cItems = classesRes.value.items || classesRes.value.data || []
        }
        if (!cItems.length) {
          // fallback to all classes if my-classes is empty
          const fallbackRes = await getAllClasses({ page_size: 200 })
          cItems = fallbackRes?.items || fallbackRes?.data || []
        }
        setClassList(cItems)

        if (cItems.length > 0) {
          const matchInit = initialClassId ? cItems.find(c => String(c.id) === String(initialClassId)) : null
          setSelectedClass(matchInit || cItems[0])
        }
      } catch (err) {
        console.error('初始化批量录入失败:', err)
      }
    }
    init()
  }, [initialClassId])

  // 2. 加载班级学生与已录成绩
  const loadClassData = useCallback(async (cls, year) => {
    if (!cls || !cls.id) return
    setLoadingStudents(true)
    try {
      const [stuRes, recRes] = await Promise.allSettled([
        getClassStudents(cls.id),
        getPhysicalTests({ class_id: cls.id, academic_year: year || selectedYear, page_size: 500 }),
      ])

      const stuList = stuRes.status === 'fulfilled' ? (stuRes.value?.items || stuRes.value?.students || (Array.isArray(stuRes.value) ? stuRes.value : [])) : []
      // 排序：男生在前，女生在后，再按学号
      const sorted = [...stuList].sort((a, b) => {
        if (a.gender !== b.gender) {
          return a.gender === 'male' || a.gender === '男' ? -1 : 1
        }
        const noA = a.student_no || a.education_id || String(a.id)
        const noB = b.student_no || b.education_id || String(b.id)
        return noA.localeCompare(noB)
      })
      setStudents(sorted)

      const recItems = recRes.status === 'fulfilled' ? (recRes.value?.items || recRes.value?.data || (Array.isArray(recRes.value) ? recRes.value : [])) : []
      const recMap = {}
      const scoreMap = {}

      recItems.forEach(r => {
        if (r.student_id) {
          recMap[r.student_id] = r
        }
      })
      setAllRecords(recMap)

      // 解析当前测试项的成绩
      sorted.forEach(s => {
        const r = recMap[s.id]
        if (selectedItem.isCompound) {
          scoreMap[s.id] = {
            height: r?.height !== undefined && r?.height !== null ? String(r.height) : '',
            weight: r?.weight !== undefined && r?.weight !== null ? String(r.weight) : '',
          }
        } else {
          const field = selectedItem.key
          scoreMap[s.id] = r && r[field] !== undefined && r[field] !== null ? String(r[field]) : ''
        }
      })

      setScores(scoreMap)
      setInitialScores(JSON.parse(JSON.stringify(scoreMap)))
      setActiveGroupIndex(0)
    } catch (err) {
      console.error('拉取班级体测数据失败:', err)
      Toast.fail('获取班级学生数据失败')
    } finally {
      setLoadingStudents(false)
    }
  }, [selectedItem, selectedYear])

  useEffect(() => {
    if (selectedClass && selectedYear) {
      loadClassData(selectedClass, selectedYear)
    }
  }, [selectedClass, selectedYear, loadClassData])

  // 当测试项目切换时更新本地显示的分数映射
  useEffect(() => {
    if (!students.length) return
    const scoreMap = {}
    students.forEach(s => {
      const r = allRecords[s.id]
      if (selectedItem.isCompound) {
        scoreMap[s.id] = {
          height: r?.height !== undefined && r?.height !== null ? String(r.height) : '',
          weight: r?.weight !== undefined && r?.weight !== null ? String(r.weight) : '',
        }
      } else {
        const field = selectedItem.key
        scoreMap[s.id] = r && r[field] !== undefined && r[field] !== null ? String(r[field]) : ''
      }
    })
    setScores(scoreMap)
    setInitialScores(JSON.parse(JSON.stringify(scoreMap)))
    setShowKeyboard(false)
  }, [selectedItemKey, students, allRecords])

  // 分组数据结构 (男生组 + 女生组)
  const groupedStudents = useMemo(() => {
    if (entryMode === 'class' || !students.length) {
      return [{ id: 'all', title: '全班学生', students }]
    }
    const males = students.filter(s => s.gender === 'male' || s.gender === '男')
    const females = students.filter(s => s.gender !== 'male' && s.gender !== '男')
    const groups = []

    for (let i = 0; i < males.length; i += groupSize) {
      groups.push({
        id: `male_${Math.floor(i / groupSize)}`,
        title: `男生第 ${Math.floor(i / groupSize) + 1} 组`,
        students: males.slice(i, i + groupSize),
      })
    }
    for (let i = 0; i < females.length; i += groupSize) {
      groups.push({
        id: `female_${Math.floor(i / groupSize)}`,
        title: `女生第 ${Math.floor(i / groupSize) + 1} 组`,
        students: females.slice(i, i + groupSize),
      })
    }
    return groups
  }, [entryMode, groupSize, students])

  // 当前激活展示的学生列表
  const displayStudents = useMemo(() => {
    if (entryMode === 'class') return students
    const group = groupedStudents[activeGroupIndex]
    return group ? group.students : students
  }, [entryMode, groupedStudents, activeGroupIndex, students])

  // 班级国家标准年级名称
  const standardGrade = useMemo(() => {
    return getStandardGradeName(selectedClass)
  }, [selectedClass])

  // 成绩变动统计
  const dirtyCount = useMemo(() => {
    return students.filter(s => {
      const cur = scores[s.id]
      const init = initialScores[s.id]
      if (selectedItem.isCompound) {
        return JSON.stringify(cur) !== JSON.stringify(init)
      }
      return cur !== init
    }).length
  }, [students, scores, initialScores, selectedItem])

  // 录入进度与等级统计
  const statistics = useMemo(() => {
    const total = students.length
    if (total === 0) return { total: 0, testedCount: 0, progress: 0, excCount: 0, goodCount: 0, passCount: 0, failCount: 0 }

    let testedCount = 0
    let excCount = 0
    let goodCount = 0
    let passCount = 0
    let failCount = 0

    students.forEach(s => {
      const val = scores[s.id]
      const res = checkStudentScorePass(s, selectedItem, val, standardGrade)
      if (res.tested) {
        testedCount++
        if (res.status === 'excellent') excCount++
        else if (res.status === 'good') goodCount++
        else if (res.status === 'pass') passCount++
        else if (res.status === 'fail' || res.status === 'absent') failCount++
      }
    })

    const progress = total > 0 ? Math.round((testedCount / total) * 100) : 0
    return {
      total,
      testedCount,
      progress,
      excCount,
      goodCount,
      passCount,
      failCount,
    }
  }, [students, scores, selectedItem, standardGrade])

  // 当前获得焦点的学生与序号
  const activeStudentIndex = useMemo(() => {
    if (!activeStudentId) return -1
    return displayStudents.findIndex(s => s.id === activeStudentId)
  }, [activeStudentId, displayStudents])

  const activeStudent = useMemo(() => {
    if (activeStudentIndex === -1) return null
    return displayStudents[activeStudentIndex]
  }, [activeStudentIndex, displayStudents])

  // 当前激活学生姓名（批量录入键盘左上角展示）
  const activeStudentName = useMemo(() => {
    if (!activeStudent) return ''
    return activeStudent.real_name || activeStudent.name || activeStudent.student_name || '学生'
  }, [activeStudent])

  // 监听录入学生切换，自动平滑居中滚动卡片至键盘上方，避免遮挡
  useEffect(() => {
    if (activeStudentId && showKeyboard) {
      const timer = setTimeout(() => {
        const el = document.getElementById(`student-row-${activeStudentId}`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [activeStudentId, showKeyboard, activeSubField])

  // 当前在键盘上展示的成绩值
  const currentInputValue = useMemo(() => {
    if (!activeStudentId) return ''
    const val = scores[activeStudentId]
    if (selectedItem.isCompound) {
      return val ? (val[activeSubField] || '') : ''
    }
    return val !== undefined && val !== null ? String(val) : ''
  }, [activeStudentId, scores, selectedItem, activeSubField])

  // 键盘修改成绩值
  const handleScoreChange = (newVal) => {
    if (!activeStudentId) return
    setScores(prev => {
      if (selectedItem.isCompound) {
        const oldVal = prev[activeStudentId] || { height: '', weight: '' }
        return {
          ...prev,
          [activeStudentId]: {
            ...oldVal,
            [activeSubField]: newVal,
          }
        }
      }
      return {
        ...prev,
        [activeStudentId]: newVal,
      }
    })
  }

  // 导航至上一位学生
  const handlePrevStudent = () => {
    if (selectedItem.isCompound && activeSubField === 'weight') {
      setActiveSubField('height')
      return
    }
    if (activeStudentIndex > 0) {
      const prevStu = displayStudents[activeStudentIndex - 1]
      setActiveStudentId(prevStu.id)
      if (selectedItem.isCompound) {
        setActiveSubField('weight')
      }
    }
  }

  // 导航至下一位学生
  const handleNextStudent = () => {
    if (selectedItem.isCompound && activeSubField === 'height') {
      setActiveSubField('weight')
      return
    }
    if (activeStudentIndex < displayStudents.length - 1) {
      const nextStu = displayStudents[activeStudentIndex + 1]
      setActiveStudentId(nextStu.id)
      if (selectedItem.isCompound) {
        setActiveSubField('height')
      }
    } else {
      setShowKeyboard(false)
      Toast.info('已到达本组末位学生')
    }
  }

  // 点击学生卡片激活录入
  const handleSelectField = (stuId, subField = 'height') => {
    setActiveStudentId(stuId)
    setActiveSubField(subField)
    setShowKeyboard(true)
  }

  // 保存成绩到后台
  const handleSaveAll = async () => {
    if (!selectedClass || saving) return
    if (dirtyCount === 0) {
      Toast.info('当前成绩无修改，无需保存')
      return
    }

    setSaving(true)
    try {
      const changedStudents = students.filter(s => {
        const cur = scores[s.id]
        const init = initialScores[s.id]
        if (selectedItem.isCompound) {
          return JSON.stringify(cur) !== JSON.stringify(init)
        }
        return cur !== init
      })

      const testRecords = changedStudents.map(s => {
        const existing = allRecords[s.id] || {}
        const base = {
          student_id: s.id,
          class_id: selectedClass.id,
          test_date: existing.test_date || new Date().toISOString().split('T')[0],
          academic_year: selectedYear,
        }

        if (selectedItem.isCompound) {
          const pair = scores[s.id] || {}
          if (pair.height) base.height = parseFloat(pair.height) || null
          if (pair.weight) base.weight = parseFloat(pair.weight) || null
        } else {
          const rawVal = scores[s.id]
          if (rawVal === '缺考' || rawVal === '免考' || rawVal === '缓考') {
            if (rawVal === '缺考') {
              base[selectedItem.key] = 0
            }
          } else {
            const isLongRun = ['run_800m', 'run_1000m', 'run_50m_8x'].includes(selectedItem.key)
            if (isLongRun || selectedItem.unit === '分·秒') {
              base[selectedItem.key] = parseTimeToSeconds(rawVal, isLongRun)
            } else {
              const parsed = parseFloat(rawVal)
              if (!isNaN(parsed)) {
                base[selectedItem.key] = parsed
              }
            }
          }
        }
        return base
      })

      await batchImportTests({
        tests: testRecords,
        mode: 'update',
        academic_year: selectedYear || selectedClass.academic_year,
      })

      Toast.success(`成功保存 ${testRecords.length} 名学生的体测成绩！`)
      setShowKeyboard(false)
      // 重新加载最新成绩并同步缓存
      await loadClassData(selectedClass, selectedYear)
    } catch (err) {
      console.error('批量保存体测成绩失败:', err)
      Toast.fail(err.response?.data?.detail || '保存成绩失败，请重试')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] text-[#0f172a] pb-32 font-sans select-none">
      {/* 1. 顶栏导航 (星翼云蓝阶页眉，完全对齐 App 质感) */}
      <div
        className="sticky top-0 z-30 px-4 pt-3 pb-3.5 shadow-sm"
        style={{ background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)' }}
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>

          <div className="text-center">
            <h1 className="text-[17.5px] font-bold text-white tracking-wide leading-tight">批量极速录入</h1>
            <p className="text-[11.5px] text-white/80 font-normal mt-0.5">全班极速遍历 · 专属数字小键盘</p>
          </div>

          <button
            onClick={() => navigate('/physical-test/standards')}
            className="w-9 h-9 rounded-full bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
            title="评分标准"
          >
            <span className="text-[11px] font-bold">标准</span>
          </button>
        </div>
      </div>

      <div className="px-3.5 pt-3 space-y-3 max-w-lg mx-auto">
        {/* 2. 班级与项目选择卡片 (双列下拉选择器) */}
        <div className="bg-white rounded-2xl p-3.5 shadow-sm border border-[#e2e8f0] space-y-2.5">
          {/* 行 1：学年与班级选择 */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setShowYearPicker(true)}
              className="flex items-center justify-between px-3 py-2 rounded-xl bg-[#f8fafc] border border-[#e2e8f0] text-[13px] font-bold text-[#0f172a] active:bg-[#f1f5f9] transition-all"
            >
              <div className="flex items-center gap-1 truncate">
                <span className="text-[11px] text-[#64748b]">学年:</span>
                <span className="truncate">{selectedYear ? `${selectedYear}学年` : '选择学年'}</span>
              </div>
              <span className="text-[10px] text-[#94a3b8] ml-1 shrink-0">▼</span>
            </button>

            <button
              type="button"
              onClick={() => setShowClassPicker(true)}
              className="flex items-center justify-between px-3 py-2 rounded-xl bg-[#f8fafc] border border-[#e2e8f0] text-[13px] font-bold text-[#0f172a] active:bg-[#f1f5f9] transition-all"
            >
              <div className="flex items-center gap-1 truncate">
                <span className="text-[11px] text-[#64748b]">班级:</span>
                <span className="truncate">{selectedClass ? selectedClass.class_name : '选择班级'}</span>
              </div>
              <span className="text-[10px] text-[#94a3b8] ml-1 shrink-0">▼</span>
            </button>
          </div>

          {/* 行 2：测试项目切换与分组模式切换 */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowItemPicker(true)}
              className="flex-1 flex items-center justify-between px-3 py-2 rounded-xl bg-[#f0fdf4] border border-[#a7f3d0] text-[13px] font-bold text-[#056b4f] active:bg-[#e8f6f0] transition-all"
            >
              <div className="flex items-center gap-1.5 truncate">
                <span className="text-[11.5px] text-[#056b4f]/70">项目:</span>
                <span className="truncate">{selectedItem.label}</span>
                {selectedItem.unit && <span className="text-[11px] text-[#64748b] font-normal">({selectedItem.unit})</span>}
              </div>
              <span className="text-[10px] text-[#056b4f] ml-1 shrink-0">▼</span>
            </button>

            {/* 模式胶囊：全班 / 分组 */}
            <div className="flex items-center p-0.5 bg-[#f1f5f9] rounded-xl shrink-0">
              <button
                type="button"
                onClick={() => setEntryMode('class')}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all ${
                  entryMode === 'class'
                    ? 'bg-[#056b4f] text-white shadow-sm'
                    : 'text-[#64748b] hover:text-[#0f172a]'
                }`}
              >
                全班
              </button>
              <button
                type="button"
                onClick={() => setEntryMode('group')}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all ${
                  entryMode === 'group'
                    ? 'bg-[#056b4f] text-white shadow-sm'
                    : 'text-[#64748b] hover:text-[#0f172a]'
                }`}
              >
                分组
              </button>
            </div>
          </div>

          {/* 分组模式下的组别切换标签栏 */}
          {entryMode === 'group' && groupedStudents.length > 0 && (
            <div className="pt-1 flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
              {groupedStudents.map((grp, idx) => (
                <button
                  key={grp.id}
                  type="button"
                  onClick={() => setActiveGroupIndex(idx)}
                  className={`px-3 py-1 rounded-full text-[11.5px] font-bold whitespace-nowrap transition-all ${
                    activeGroupIndex === idx
                      ? 'bg-[#0f172a] text-white shadow-sm'
                      : 'bg-[#f1f5f9] text-[#64748b] active:bg-[#e2e8f0]'
                  }`}
                >
                  {grp.title} ({grp.students.length}人)
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 3. 实时进度与达标看板 */}
        <div className="bg-white rounded-2xl p-3.5 shadow-sm border border-[#e2e8f0] space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#00b578]"></span>
              <span className="text-[13.5px] font-bold text-[#0f172a]">
                已录入 {statistics.testedCount} / {statistics.total} 人
              </span>
            </div>
            <span className="text-[13px] font-extrabold text-[#056b4f]">
              {statistics.progress}%
            </span>
          </div>

          {/* 进度条 */}
          <div className="w-full h-2 bg-[#f1f5f9] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#056b4f] to-[#00b578] transition-all duration-300 rounded-full"
              style={{ width: `${statistics.progress}%` }}
            />
          </div>

          {/* 实时等级分布徽章 */}
          <div className="grid grid-cols-4 gap-1.5 text-center pt-1 text-[11px]">
            <div className="p-1.5 rounded-xl bg-[#ecfdf5] border border-[#a7f3d0] text-[#059669] font-bold">
              优秀 {statistics.excCount}人
            </div>
            <div className="p-1.5 rounded-xl bg-[#f0fdf4] border border-[#a7f3d0] text-[#056b4f] font-bold">
              良好 {statistics.goodCount}人
            </div>
            <div className="p-1.5 rounded-xl bg-[#f0fdfa] border border-[#a5f3fc] text-[#0891b2] font-bold">
              及格 {statistics.passCount}人
            </div>
            <div className="p-1.5 rounded-xl bg-[#fff1f2] border border-[#fecdd3] text-[#e11d48] font-bold">
              不及格 {statistics.failCount}人
            </div>
          </div>
        </div>

        {/* 4. 学生名册与极速录入列表 */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-[12px] text-[#64748b] px-1 font-semibold">
            <span>当前名单: {displayStudents.length} 名学生</span>
            <span>点击分数框唤起专用小键盘</span>
          </div>

          {loadingStudents ? (
            <div className="bg-white rounded-2xl p-8 text-center text-[#94a3b8] text-[13px] border border-[#e2e8f0]">
              正在加载学生名册...
            </div>
          ) : displayStudents.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center text-[#94a3b8] text-[13px] border border-[#e2e8f0]">
              暂无匹配的学生名册
            </div>
          ) : (
            displayStudents.map((stu, idx) => {
              const isMale = stu.gender === 'male' || stu.gender === '男'
              const isRowFocused = activeStudentId === stu.id
              const scoreVal = scores[stu.id]
              const res = checkStudentScorePass(stu, selectedItem, scoreVal, standardGrade)

              return (
                <div
                  key={stu.id}
                  id={`student-row-${stu.id}`}
                  className={`bg-white rounded-2xl p-3 shadow-sm border transition-all flex items-center gap-2.5 relative overflow-hidden ${
                    isRowFocused
                      ? 'border-[#056b4f] ring-2 ring-[#056b4f]/20 shadow-[0_4px_16px_rgba(5,107,79,0.12)]'
                      : 'border-[#e2e8f0]'
                  }`}
                >
                  {/* 性别指示侧条 (男蓝/女粉) */}
                  <div
                    className={`absolute left-0 top-0 bottom-0 w-1 ${
                      isMale ? 'bg-[#056b4f]' : 'bg-[#ec4899]'
                    }`}
                  />

                  {/* 序号与学生基础信息 */}
                  <div
                    onClick={() => handleSelectField(stu.id, 'height')}
                    className="flex-1 min-w-0 pl-1.5 cursor-pointer"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-[12px] font-extrabold text-[#94a3b8]">#{idx + 1}</span>
                      <span className="text-[14.5px] font-bold text-[#0f172a] truncate">
                        {stu.real_name || stu.name || stu.student_name}
                      </span>
                      <span
                        className={`text-[10px] px-1 py-0.2 rounded font-bold ${
                          isMale ? 'bg-[#e8f6f0] text-[#056b4f]' : 'bg-[#fce7f3] text-[#ec4899]'
                        }`}
                      >
                        {isMale ? '男' : '女'}
                      </span>
                    </div>

                    <div className="text-[11px] text-[#64748b] mt-0.5 truncate">
                      {stu.education_id || stu.student_no ? `ID: ${stu.education_id || stu.student_no}` : (stu.class_name || '主校区')}
                    </div>
                  </div>

                  {/* 录入得分框 (支持复合项身高/体重双格) */}
                  {selectedItem.isCompound ? (
                    <div className="flex items-center gap-1.5">
                      {/* 身高框 */}
                      <div
                        onClick={() => handleSelectField(stu.id, 'height')}
                        className={`w-16 h-10 rounded-xl border flex flex-col items-center justify-center cursor-pointer transition-all ${
                          isRowFocused && activeSubField === 'height'
                            ? 'bg-[#f0fdf4] border-[#056b4f] shadow-sm'
                            : 'bg-[#f8fafc] border-[#e2e8f0]'
                        }`}
                      >
                        <span className="text-[10px] text-[#94a3b8] leading-none">身高</span>
                        <div className="flex items-center text-[13.5px] font-extrabold text-[#0f172a] leading-tight">
                          <span>{scoreVal?.height || '--'}</span>
                          {isRowFocused && activeSubField === 'height' && (
                            <span className="w-0.5 h-3.5 bg-[#056b4f] animate-pulse ml-0.5"></span>
                          )}
                        </div>
                      </div>

                      {/* 体重框 */}
                      <div
                        onClick={() => handleSelectField(stu.id, 'weight')}
                        className={`w-16 h-10 rounded-xl border flex flex-col items-center justify-center cursor-pointer transition-all ${
                          isRowFocused && activeSubField === 'weight'
                            ? 'bg-[#f0fdf4] border-[#056b4f] shadow-sm'
                            : 'bg-[#f8fafc] border-[#e2e8f0]'
                        }`}
                      >
                        <span className="text-[10px] text-[#94a3b8] leading-none">体重</span>
                        <div className="flex items-center text-[13.5px] font-extrabold text-[#0f172a] leading-tight">
                          <span>{scoreVal?.weight || '--'}</span>
                          {isRowFocused && activeSubField === 'weight' && (
                            <span className="w-0.5 h-3.5 bg-[#056b4f] animate-pulse ml-0.5"></span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* 单项得分框 */
                    <div
                      onClick={() => handleSelectField(stu.id, 'val')}
                      className={`min-w-[76px] h-10 px-2 rounded-xl border flex items-center justify-center gap-1 cursor-pointer transition-all ${
                        isRowFocused
                          ? 'bg-[#f0fdf4] border-[#056b4f] shadow-sm'
                          : 'bg-[#f8fafc] border-[#e2e8f0]'
                      }`}
                    >
                      <span className="text-[14.5px] font-extrabold text-[#0f172a]">
                        {scoreVal !== undefined && scoreVal !== null && scoreVal !== '' ? String(scoreVal) : '--'}
                      </span>
                      {selectedItem.unit && (
                        <span className="text-[10.5px] text-[#94a3b8]">{selectedItem.unit}</span>
                      )}
                      {isRowFocused && (
                        <span className="w-0.5 h-4 bg-[#056b4f] animate-pulse ml-0.5"></span>
                      )}
                    </div>
                  )}

                  {/* 实时国家标准评分标签 */}
                  <div
                    className="w-16 text-center py-1 rounded-xl border flex flex-col items-center justify-center shrink-0"
                    style={{
                      backgroundColor: res.gradeBg,
                      borderColor: res.gradeBorder,
                      color: res.scoreColor,
                    }}
                  >
                    <span className="text-[12px] font-extrabold leading-tight">
                      {res.scoreText}
                    </span>
                    <span className="text-[10px] font-semibold leading-tight mt-0.5">
                      {res.gradeText}
                    </span>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* 5. 底部固定保存栏 (当小键盘关闭时展示) */}
      {!showKeyboard && (
        <div className="fixed bottom-0 left-0 right-0 p-3.5 bg-white/95 backdrop-blur-md border-t border-[#e2e8f0] z-40 max-w-lg mx-auto">
          <button
            type="button"
            disabled={saving || dirtyCount === 0}
            onClick={handleSaveAll}
            className={`w-full h-12 rounded-full font-bold text-[15.5px] text-white flex items-center justify-center gap-2 transition-all shadow-md ${
              saving || dirtyCount === 0
                ? 'bg-[#94a3b8] cursor-not-allowed'
                : 'bg-gradient-to-r from-[#056b4f] to-[#04523c] active:opacity-90 active:scale-[0.99]'
            }`}
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                <span>一键保存成绩 {dirtyCount > 0 ? `(${dirtyCount}条待保存)` : ''}</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* 6. 体育专属数字小键盘 (极速录入) */}
      <SportScoreKeyboard
        visible={showKeyboard && !!activeStudent}
        activeField={selectedItem.isCompound ? activeSubField : selectedItem.key}
        fieldName={
          selectedItem.isCompound
            ? `${activeStudentName} · ${activeSubField === 'height' ? '身高' : '体重'}`
            : activeStudentName
        }
        unit=""
        value={currentInputValue}
        itemProgressText={`#${activeStudentIndex + 1}`}
        onChangeValue={handleScoreChange}
        onPrevStudent={handlePrevStudent}
        onNextStudent={handleNextStudent}
        hasPrevStudent={activeStudentIndex > 0 || (selectedItem.isCompound && activeSubField === 'weight')}
        hasNextStudent={activeStudentIndex < displayStudents.length - 1 || (selectedItem.isCompound && activeSubField === 'height')}
        prevButtonText={selectedItem.isCompound && activeSubField === 'weight' ? '上一项' : '上一位'}
        nextButtonText={selectedItem.isCompound && activeSubField === 'height' ? '下一项' : '下一位'}
        studentInfo={activeStudent ? {
          name: activeStudentName,
          index: activeStudentIndex,
          total: displayStudents.length,
        } : null}
        showStudentBar={true}
        showZeroButton={true}
        onClose={() => setShowKeyboard(false)}
      />

      {/* 学年选择弹窗 */}
      <Popup
        visible={showYearPicker}
        position="bottom"
        round
        onClose={() => setShowYearPicker(false)}
      >
        <Picker
          title="选择学年"
          columns={academicYears.map(y => ({ text: `${y} 学年`, value: y }))}
          onConfirm={(val) => {
            setSelectedYear(val)
            localStorage.setItem('current_academic_year', val)
            setShowYearPicker(false)
          }}
          onCancel={() => setShowYearPicker(false)}
        />
      </Popup>

      {/* 班级选择弹窗 */}
      <Popup
        visible={showClassPicker}
        position="bottom"
        round
        onClose={() => setShowClassPicker(false)}
      >
        <Picker
          title="选择班级"
          columns={classList.map(c => ({
            text: `${c.grade || ''} ${c.class_name}`,
            value: String(c.id),
          }))}
          onConfirm={(val) => {
            const found = classList.find(c => String(c.id) === String(val))
            if (found) setSelectedClass(found)
            setShowClassPicker(false)
          }}
          onCancel={() => setShowClassPicker(false)}
        />
      </Popup>

      {/* 项目选择弹窗 */}
      <Popup
        visible={showItemPicker}
        position="bottom"
        round
        onClose={() => setShowItemPicker(false)}
      >
        <Picker
          title="选择体测项目"
          columns={TEST_ITEMS.map(it => ({
            text: `${it.label}${it.unit ? ` (${it.unit})` : ''}`,
            value: it.key,
          }))}
          onConfirm={(val) => {
            setSelectedItemKey(val)
            setShowItemPicker(false)
          }}
          onCancel={() => setShowItemPicker(false)}
        />
      </Popup>
    </div>
  )
}
